import { useState } from "react";
import { Controller } from "react-hook-form";
import { Alert } from "@/components/alert";
import { Button } from "@/components/button";
import { Toggle } from "@/components/toggle";
import { Modal } from "@/components/modal";
import { Form, FormField, FormSubmit } from "@/src/forms";
import { createApiToken, type ApiToken } from "@/src/services/api_tokens_service";
import { createTokenSchema, type CreateTokenValues } from "../_schemas/create-token";

type Props = {
  open: boolean;
  onClose: () => void;
  /** Fired after the backend creates the token; the parent reloads the list + shows a success toast. */
  onCreate: (token: ApiToken) => void;
  /** Forwarded to Modal — the outlet context's `mainInsetLeft`, so the dialog
   *  centers relative to the sidebar-aware content area. */
  insetLeft?: number;
};

export function CreateTokenModal({ open, onClose, onCreate, insetLeft }: Props) {
  const [createError, setCreateError] = useState<string | null>(null);

  async function handleValid(values: CreateTokenValues) {
    setCreateError(null);
    const res = await createApiToken(values.sandbox, values.name);
    if (!res.ok) {
      setCreateError(res.error.message);
      return;
    }
    onCreate(res.value);
    handleClose();
  }

  function handleClose() {
    setCreateError(null);
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Create API token"
      description="Choose an environment for this token."
      insetLeft={insetLeft}
    >
      <Form
        schema={createTokenSchema}
        defaultValues={{ name: "", sandbox: false }}
        onSubmit={handleValid}
        className="flex flex-col gap-4"
      >
        {createError && (
          <Alert tone="danger" role="alert">
            {createError}
          </Alert>
        )}
        <FormField name="name" label="Name" placeholder="e.g. Production backend" />
        <div className="flex items-center justify-between gap-4 rounded-[4px] bg-grey-50 px-3 py-2">
          <div className="flex flex-col">
            <span className="text-small font-medium text-black">Sandbox</span>
            <span className="text-tiny text-grey-900">
              Issue a token scoped to the sandbox environment instead of live.
            </span>
          </div>
          <Controller
            name="sandbox"
            render={({ field }) => (
              <Toggle checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />
            )}
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button hierarchy="secondary" type="button" onClick={handleClose}>
            Cancel
          </Button>
          <FormSubmit hierarchy="primary" pendingLabel="Creating…">
            Create token
          </FormSubmit>
        </div>
      </Form>
    </Modal>
  );
}
